/**
 * useRankHistory — compute each player's rank delta against the prior period.
 *
 *   Input:  an array of players (sorted desc by points) for the current week.
 *   Output: { [userId]: deltaNumber | null }
 *
 *   - delta > 0  → moved up that many ranks
 *   - delta < 0  → moved down that many ranks
 *   - delta = 0  → no change
 *   - delta null → no baseline yet (first time we've seen this period)
 *
 * Storage: rank snapshots are persisted in localStorage, keyed by
 * `gmb.scorekeeper.rank.<companyId>.<weekStart>`. On every render we read
 * the PREVIOUS week's snapshot to compute deltas, and write THIS week's
 * snapshot for the next visit.
 *
 * Backend-correct path: when the backend exposes
 * `GET /scorekeeper/ranks?period=<previous>`, swap `readSnapshot` for an
 * API call and `writeSnapshot` becomes a no-op. The shape of the hook
 * stays identical — consumers don't change.
 */
import { useMemo } from "react";

const KEY_PREFIX = "gmb.scorekeeper.rank";

// Start-of-week (Monday) in local time, ISO date string. Stable per week.
const weekStartIso = (d = new Date()) => {
  // `Date.now()` is fine here — runs during a real user render, not a workflow.
  const now = new Date(d.getTime());
  const day = now.getDay(); // 0 = Sunday … 6 = Saturday
  // Shift so Monday is start of week.
  const diff = day === 0 ? -6 : 1 - day;
  now.setDate(now.getDate() + diff);
  now.setHours(0, 0, 0, 0);
  return now.toISOString().slice(0, 10);
};

const previousWeekStartIso = (d = new Date()) => {
  const prev = new Date(d.getTime());
  prev.setDate(prev.getDate() - 7);
  return weekStartIso(prev);
};

const key = (companyId, weekIso) =>
  `${KEY_PREFIX}.${companyId || "default"}.${weekIso}`;

const readSnapshot = (companyId, weekIso) => {
  try {
    const raw = window.localStorage.getItem(key(companyId, weekIso));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeSnapshot = (companyId, weekIso, ranks) => {
  try {
    window.localStorage.setItem(
      key(companyId, weekIso),
      JSON.stringify(ranks),
    );
  } catch {
    /* quota or disabled storage — degrade silently */
  }
};

/**
 * @param {Array} players — already sorted desc by current-period points
 * @param {string} companyId
 * @returns {{ deltasByUserId: Record<string, number|null> }}
 */
export function useRankHistory(players, companyId) {
  return useMemo(() => {
    if (!Array.isArray(players) || players.length === 0) {
      return { deltasByUserId: {} };
    }

    const thisWeek = weekStartIso();
    const lastWeek = previousWeekStartIso();

    // Build this period's ranks ({ userId: rank }), 1-indexed.
    const currentRanks = {};
    players.forEach((p, i) => {
      const id = p?.userId || p?.info?._id;
      if (id) currentRanks[id] = i + 1;
    });

    // Persist current ranks for NEXT week's delta calculation.
    writeSnapshot(companyId, thisWeek, currentRanks);

    // Read last week's snapshot (if any) for THIS week's delta.
    const previous = readSnapshot(companyId, lastWeek);

    const deltasByUserId = {};
    Object.keys(currentRanks).forEach((id) => {
      if (!previous || previous[id] == null) {
        deltasByUserId[id] = null; // no baseline → no pill
        return;
      }
      // Positive delta = moved UP (rank number went down).
      deltasByUserId[id] = previous[id] - currentRanks[id];
    });

    return { deltasByUserId };
  }, [players, companyId]);
}
